import CustomTypography from "../../../Components/Typography";
import Box from "@mui/material/Box";
import { Card, Typography } from "@mui/material";
import InputField from "../../../Components/InputField";
import muiStyles from "../styles/muiStyles";
import { useFormikContext } from "formik";
import ImageUploader from "../../../Components/ImageUploader";
import { useTranslation } from "react-i18next";

const IMAGE_FIELDS = [
  {
    type: "services",
    labelKey: "services_image",
    urlField: "servicesImageUrl",
    tempField: "_servicesImageTempFile",
    deletedField: "_servicesImageDeleted",
    initialField: "_servicesImageInitialUrl",
  },
  {
    type: "store",
    labelKey: "store_image",
    urlField: "storeImageUrl",
    tempField: "_storeImageTempFile",
    deletedField: "_storeImageDeleted",
    initialField: "_storeImageInitialUrl",
  },
  {
    type: "restaurant",
    labelKey: "restaurant_image",
    urlField: "restaurantImageUrl",
    tempField: "_restaurantImageTempFile",
    deletedField: "_restaurantImageDeleted",
    initialField: "_restaurantImageInitialUrl",
  },
];

// One section per business type. Each carries its own name, description and
// image. The general `description` isn't edited here — it round-trips unchanged.
const SECTIONS = [
  { type: "services", titleKey: "shine_services_title", nameField: "servicesName", descField: "servicesDescription", image: IMAGE_FIELDS[0] },
  { type: "store", titleKey: "shine_store_title", nameField: "storeName", descField: "storeDescription", image: IMAGE_FIELDS[1] },
  { type: "restaurant", titleKey: "shine_restaurant_title", nameField: "restaurantName", descField: "restaurantDescription", image: IMAGE_FIELDS[2] },
];

const MakeBusinessShine = ({ isloading, setSubmitStatus, showRestaurant = true }) => {
  const { t } = useTranslation("page-business-settings", "dropdowns");
  const { handleChange, values, setFieldValue } = useFormikContext();

  const handleImageError = (message) => {
    setSubmitStatus({
      open: true,
      message: message || t("image_upload_failed"),
      severity: "error",
    });
  };

  const { cardStyles } = muiStyles;

  const sections = SECTIONS.filter(
    (s) => s.type !== "restaurant" || showRestaurant
  );

  return (
    <Card sx={cardStyles}>
      <CustomTypography type="subheading">
        {t("make_your_business_shine")}
      </CustomTypography>

      {sections.map((section, index) => (
        <Box
          key={section.type}
          sx={{
            pt: index === 0 ? "24px" : "28px",
            mt: index === 0 ? 0 : "28px",
            borderTop: index === 0 ? "none" : "1px solid rgba(234, 234, 234, 1)",
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 600,
              color: "rgba(47, 43, 61, 0.9)",
            }}
          >
            {t(section.titleKey)}
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr auto" },
              columnGap: "56px",
              rowGap: "20px",
              alignItems: "start",
              mt: "16px",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", maxWidth: "520px" }}>
              <InputField
                name={section.nameField}
                label={t("business_name_label")}
                value={values[section.nameField] || ""}
                onChange={handleChange}
                placeholder={t("business_name_placeholder")}
                disabled={isloading}
                maxLength={80}
              />
              <InputField
                name={section.descField}
                label={t("business_description_label")}
                value={values[section.descField] || ""}
                onChange={handleChange}
                placeholder={t("business_description_placeholder")}
                disabled={isloading}
                multiline
                minRows={3}
                maxRows={4}
                maxLength={200}
              />
            </Box>

            <ImageUploader
              label={t("business_image_label")}
              imageUrl={values[section.image.urlField]}
              setImageUrl={(url) => setFieldValue(section.image.urlField, url)}
              tempImageFile={values[section.image.tempField]}
              setTempImageFile={(file) => setFieldValue(section.image.tempField, file)}
              onError={handleImageError}
              isLoading={isloading}
              maxSizeMB={15}
              hint={t("upload_logo_hint")}
              imageStyle={{
                width: 110,
                height: 110,
                borderRadius: "50%",
                objectFit: "cover",
              }}
              initialImageUrl={values[section.image.initialField]}
              onImageDelete={() => setFieldValue(section.image.deletedField, true)}
              resetImageDeleted={() => setFieldValue(section.image.deletedField, false)}
            />
          </Box>
        </Box>
      ))}
    </Card>
  );
};

export const BUSINESS_IMAGE_FIELDS = IMAGE_FIELDS;

export default MakeBusinessShine;
